import { useEffect, useRef } from 'react'
import {
  UIDialogContent,
  UIDialogHeader,
  UIDialogResponsive,
} from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { useLocalizedStringPairGetter } from 'src/i18n/localizedContent'
import type { CollectionCardSetProgress } from 'src/services/apollo/generated/graphql'
import { useTrackPageView } from 'src/utils/tracker'
import type { CollectionCardSetDataType } from '../UserDecoration/schema/collectionCard'
import {
  getCollectionCardProgressString,
  getSortedTotalCollectionCards,
} from '../UserDecoration/utils/collectionCard'
import { CollectionCardItem } from './CollectionCardItem'

export function CollectionCardSetDialog(props: {
  open: boolean
  onClose: () => void
  collectionCardSetProgress: CollectionCardSetProgress
  collectionCardSetData: CollectionCardSetDataType
}) {
  const { collectionCardSetProgress, collectionCardSetData } = props
  const getText = useLocalizedStringPairGetter()
  const progressString = getCollectionCardProgressString(
    props.collectionCardSetProgress,
  )

  const trackPageView = useTrackPageView()
  const trackPageViewRef = useRef(trackPageView)
  trackPageViewRef.current = trackPageView

  useEffect(() => {
    if (props.open) {
      trackPageViewRef.current({
        scene: 'collection-card',
        modal: true,
        // Track card set's English name
        section: getText(props.collectionCardSetData.name, 'en'),
      })
    }
  }, [props.open])

  return (
    <UIDialogResponsive
      dismissable
      open={props.open}
      onClose={props.onClose}
      className="max-w-[900px] w-screen mmd:max-h-[80vh] mmd:flex mmd:flex-col"
      modalClassName="h-[calc(var(--visual-viewport-height)*0.75)] mt-auto mmd:h-auto mmd:mt-0"
    >
      <UIDialogHeader
        heading={
          <div className="flex items-center justify-between">
            <span>{getText(collectionCardSetData.name)}</span>
            <span className="text-neutral-500 dark:text-neutral-300 text-sm">
              <Text
                i18nKey={
                  'user:user-decoration.collection-card.card-set.progress'
                }
              />
              <Text i18nKey={'common:punctuations.colon'} /> {progressString}
            </span>
          </div>
        }
      ></UIDialogHeader>
      <UIDialogContent className="mmd:overflow-auto mmd:min-h-0 mmd:flex-1">
        <CollectionCardSetDialogContent
          collectionCardSetProgress={collectionCardSetProgress}
          collectionCardSetData={collectionCardSetData}
        />
      </UIDialogContent>
    </UIDialogResponsive>
  )
}

function CollectionCardSetDialogContent(props: {
  collectionCardSetProgress: CollectionCardSetProgress
  collectionCardSetData: CollectionCardSetDataType
}) {
  const { collectionCardSetProgress } = props
  const { totalCollectionCards, ownedCollectionCards } =
    collectionCardSetProgress

  const totalCards = getSortedTotalCollectionCards(totalCollectionCards)
  const getText = useLocalizedStringPairGetter()

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-x-6 gap-y-8">
      {totalCards.map(card => (
        <div key={card.id} className="relative rounded-md overflow-hidden">
          <CollectionCardItem
            collectionCard={card}
            owned={ownedCollectionCards.some(c => c.id === card.id)}
            showOverlay
            trackingArgs={{
              cardSetName: getText(props.collectionCardSetData.name, 'en'),
            }}
          />
        </div>
      ))}
    </div>
  )
}
